import { ModelFactory } from "../../ModelFactory";

export class DNSZone {
  constructor(public readonly records: DNSRecord[]) {}
}

class DNSZoneFactory extends ModelFactory<DNSZone> {
  protected fromJson(json: any): DNSZone {
    return new DNSZone(dnsRecordFactory.fromItemsResponse(json));
  }
}
export const dnsZoneFactory = new DNSZoneFactory();

export class BasicDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "TXT" | "NS",
    public readonly name: string,
    public readonly content: string,
    public readonly ttl: number,
  ) {}
}
export class RoutingDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "A" | "AAAA" | "CNAME",
    public readonly name: string,
    public readonly content: string,
    public readonly ttl: number,
    public readonly proxied: boolean,
  ) {}
}
export class PriorityDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "MX" | "REDIRECT",
    public readonly name: string,
    public readonly content: string,
    public readonly priority: number,
    public readonly ttl: number,
  ) {}
}
export class SrvDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "SRV",
    public readonly service: string,
    public readonly proto: string,
    public readonly name: string,
    public readonly priority: number,
    public readonly weight: number,
    public readonly port: number,
    public readonly target: string,
    public readonly ttl: number,
  ) {}
}
export class CaaDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "CAA",
    public readonly name: string,
    public readonly flags: number,
    public readonly tag: string,
    public readonly value: string,
    public readonly ttl: number,
  ) {}
}
export class TlsaDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "TLSA",
    public readonly name: string,
    public readonly usage: number,
    public readonly selector: number,
    public readonly matchingType: number,
    public readonly certificate: string,
    public readonly ttl: number,
  ) {}
}
export class HttpsDNSRecord {
  constructor(
    public readonly id: string,
    public readonly type: "HTTPS",
    public readonly name: string,
    public readonly priority: number,
    public readonly target: string,
    public readonly value: string,
    public readonly ttl: number,
  ) {}
}
export type DNSRecord =
  | BasicDNSRecord
  | RoutingDNSRecord
  | PriorityDNSRecord
  | SrvDNSRecord
  | CaaDNSRecord
  | TlsaDNSRecord
  | HttpsDNSRecord;
export type DNSRecordType =
  | BasicDNSRecord["type"]
  | RoutingDNSRecord["type"]
  | PriorityDNSRecord["type"]
  | SrvDNSRecord["type"]
  | CaaDNSRecord["type"]
  | TlsaDNSRecord["type"]
  | HttpsDNSRecord["type"];
class DNSRecordFactory extends ModelFactory<DNSRecord> {
  protected fromJson(json: any): DNSRecord {
    if (["TXT", "NS"].includes(json["type"])) {
      return new BasicDNSRecord(json["id"], json["type"], json["name"], json["content"], json["ttl"]);
    }
    if (["A", "AAAA", "CNAME"].includes(json["type"])) {
      return new RoutingDNSRecord(
        json["id"],
        json["type"],
        json["name"],
        json["content"],
        json["ttl"],
        json["proxied"],
      );
    }
    if (["MX", "REDIRECT"].includes(json["type"])) {
      return new PriorityDNSRecord(
        json["id"],
        json["type"],
        json["name"],
        json["content"],
        json["priority"],
        json["ttl"],
      );
    }
    if (["SRV"].includes(json["type"])) {
      return new SrvDNSRecord(
        json["id"],
        json["type"],
        json["data"]["service"],
        json["data"]["proto"],
        json["data"]["name"],
        json["data"]["priority"],
        json["data"]["weight"],
        json["data"]["port"],
        json["data"]["target"],
        json["ttl"],
      );
    }
    if (["CAA"].includes(json["type"])) {
      return new CaaDNSRecord(
        json["id"],
        json["type"],
        json["name"],
        json["data"]["flags"],
        json["data"]["tag"],
        json["data"]["value"],
        json["ttl"],
      );
    }
    if (["TLSA"].includes(json["type"])) {
      return new TlsaDNSRecord(
        json["id"],
        json["type"],
        json["name"],
        json["data"]["usage"],
        json["data"]["selector"],
        json["data"]["matching_type"],
        json["data"]["certificate"],
        json["ttl"],
      );
    }
    if (["HTTPS"].includes(json["type"])) {
      return new HttpsDNSRecord(
        json["id"],
        json["type"],
        json["name"],
        json["data"]["priority"],
        json["data"]["target"],
        json["data"]["value"],
        json["ttl"],
      );
    }
    throw new Error(`Unknown DNS record type: ${json["type"]}`);
  }
}
export const dnsRecordFactory = new DNSRecordFactory();
